'use client';

import isEqual from 'lodash/isEqual';
import dynamic from 'next/dynamic';
import { redirect, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import slugify from 'react-slugify';

import {
  comfortOptions,
  driveTypeOptions,
  fuelTypeOptions,
  otherOptions,
  safetyAndTechOptions,
  transmissionTypeOptions,
} from '@/constants/filters/filters.constants';
import { useDealershipCookie } from '@/hooks/dealership/dealership.hook';
import { useBodyTypesFilter } from '@/hooks/filters/body-types-filter.hook';
import { useBrandsRangesFilter } from '@/hooks/filters/brands-ranges-filter.hook';
import { useColorsFilter } from '@/hooks/filters/colors-filter.hook';
import { useDoorsFilter } from '@/hooks/filters/doors-filter.hook';
import { useDrivetrainFilter } from '@/hooks/filters/drivetrain-filter.hook';
import { useFeaturesFilter } from '@/hooks/filters/features-filter.hook';
import { useFinanceFilter } from '@/hooks/filters/finance-filter.hook';
import { useFuelConsumptionFilter } from '@/hooks/filters/fuel-consumption.hook';
import { useFuelTypeFilter } from '@/hooks/filters/fuel-type-filter.hook';
import { useMileageFilter } from '@/hooks/filters/mileage-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useTransmissionTypeFilter } from '@/hooks/filters/transmission-type-filter.hook';
import { useYearsFilterHook } from '@/hooks/filters/years-filter.hook';
import { useRoutesConfig } from '@/hooks/routes-config';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';
import { useProvincesDealershipsFilter } from '@/hooks/used-cars/provinces-dealerships-filter.hook';
import { useQueryParams } from '@/hooks/used-cars/query-params.hook';
import { useVehicles } from '@/hooks/used-cars/vehicles.hook';
import {
  getModelsPagePath,
  getSearchResultPath,
} from '@/services/link.service';
import { parseSearchParams } from '@/services/QueryStringService';
import { getVehiclesWithFilters } from '@/services/used-cars/used-cars.service';
import { ModelPageProps, VehicleType } from '@/types/used-cars/used-cars.types';

import { BlockWrapper } from '../block-wrapper/block-wrapper';
import { Breadcrumbs } from '../breadcrumbs/breadcrumbs';
import { LandingChildrenList } from '../landing-children-list/landing-children-list';
import { PageDescription } from '../opening-paragraph/page-description/page-description';
import { MobileResults } from '../search/mobile-results/mobile-results';
import { NewSearchFilters } from '../search/search-filters/new-search-filters';
import { PageTitle } from '../typography/page-title/page-title';

const SearchFilters = dynamic(() =>
  import('../search/search-filters/search-filters').then(
    (filter) => filter.SearchFilters,
  ),
);

const SearchResults = dynamic(() =>
  import('../search/search-results/search-results').then(
    (mod) => mod.SearchResults,
  ),
);

const Model: React.FC<ModelPageProps> = ({
  carType,
  brands,
  defaultCarPrice,
  bodyTypes,
  colourTypes,
  doorsTypes,
  defaultCarYears,
  defaultCarMileages,
  provincesWithDealerships,
  vehicles,
  vehiclesMeta,
  defaultConsumption,
  activeBrand,
  activeModel,
  fuelTypeCount,
  transmissionCount,
  driveTrainCount,
  featuresCount,
  openingParagraph,
  newFilters,
}) => {
  const { setColorFilters } = useColorsFilter();
  const { setDoorsFilter } = useDoorsFilter();
  const { setBodyTypesFilter } = useBodyTypesFilter();
  const { setDefaultPriceFilter } = usePriceFilter();
  const { setYearsFilterDefault } = useYearsFilterHook();
  const {
    setBrandsRangesFilter,
    resetBrandsRanges,
    brandsRangesSelectedFilters,
    toggleBrandRange,
  } = useBrandsRangesFilter();
  const { setFeaturesFilter, setFeaturesCount } = useFeaturesFilter();
  const { setProvincesDealershipsFilter } = useProvincesDealershipsFilter();
  const { setTransmissionTypeFilter, setTransmissionCount } =
    useTransmissionTypeFilter();
  const { setFuelTypeFilter, setFuelTypeCount } = useFuelTypeFilter();
  const { setDrivetrainTypesFilter, setDriveTrainCount } =
    useDrivetrainFilter();
  const { selectFiltersFromQueryParams } = useQueryParams();
  const {
    setVehicles,
    setVehiclesMeta,
    vehicles: vehiclesState,
    vehiclesMeta: vehiclesMetaData,
  } = useVehicles();
  const { resetFinanceState } = useFinanceFilter();
  const { setMileageFilterDefault } = useMileageFilter();
  const { setConsumptionFilterDefault } = useFuelConsumptionFilter();
  const {
    hasFiltersQuery,
    filtersQuery,
    isInitialQuery,
    setIsInitialQuery,
    activeCarType,
    setActiveCarType,
    isCleared,
    setIsCleared,
  } = useFiltersQuery();
  const routes = useRoutesConfig();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [canBeRedirected, setCanBeRedirected] = useState(false);

  const isFirstRender = useRef(true);
  const { kiaDealershipId } = useDealershipCookie();

  if (!activeBrand) {
    redirect('/used-cars');
  }

  useEffect(() => {
    setActiveCarType(carType as any);
    if (isInitialQuery) setIsInitialQuery(false);
    setBrandsRangesFilter(brands);
  }, []);

  useEffect(() => {
    setBodyTypesFilter(bodyTypes);
  }, [bodyTypes]);

  useEffect(() => {
    setDefaultPriceFilter(defaultCarPrice);
    setColorFilters(colourTypes);
    setDoorsFilter(doorsTypes);
    setFeaturesFilter((features) => ({
      ...features,
      comfort: comfortOptions,
      safetyAndTech: safetyAndTechOptions,
      other: otherOptions,
    }));
    setFeaturesCount(featuresCount as any);
    setYearsFilterDefault(defaultCarYears);
    setMileageFilterDefault(defaultCarMileages);
    setConsumptionFilterDefault(defaultConsumption);
    setProvincesDealershipsFilter(provincesWithDealerships);
    setTransmissionTypeFilter(transmissionTypeOptions);
    setTransmissionCount(transmissionCount);
    setFuelTypeFilter(fuelTypeOptions);
    setFuelTypeCount(fuelTypeCount);
    setDrivetrainTypesFilter(driveTypeOptions);
    setDriveTrainCount(driveTrainCount);
    setVehiclesMeta(vehiclesMeta);
    resetFinanceState();
    setIsCleared(false);
  }, [featuresCount, transmissionCount, fuelTypeCount, driveTrainCount]);

  useEffect(() => {
    setVehicles(vehicles);
    selectFiltersFromQueryParams(parseSearchParams(searchParams, { depth: 7 }));
    isFirstRender.current = true;
  }, [vehicles, searchParams]);

  useEffect(() => {
    setVehiclesMeta(vehiclesMeta);
    resetBrandsRanges(null);
    toggleBrandRange(activeModel as any);
    setCanBeRedirected(true);
  }, [activeModel]);

  useEffect(() => {
    if (canBeRedirected) {
      const needRedirect =
        !brandsRangesSelectedFilters.length ||
        brandsRangesSelectedFilters.length > 1;

      if (needRedirect) {
        router.push(getSearchResultPath(activeCarType, routes));
      }
    }
  }, [brandsRangesSelectedFilters]);

  useEffect(() => {
    if (!isFirstRender.current) {
      if (filtersQuery && (hasFiltersQuery || isCleared)) {
        const meta = {
          ...vehiclesMetaData,
        };

        getVehiclesWithFilters(
          filtersQuery,
          meta,
          undefined,
          activeCarType,
        ).then(({ vehicles: newVehicles, meta: newMeta }) => {
          if (!isEqual(newVehicles, vehiclesState)) {
            setVehicles(newVehicles);
          }
          if (meta.filterCount !== newMeta.filter_count) {
            setVehiclesMeta({ ...meta, filterCount: newMeta.filter_count });
          }
        });
      } else {
        setIsCleared(false);
      }
    } else {
      isFirstRender.current = false;
    }
  }, [filtersQuery]);

  return (
    <React.Fragment>
      <BlockWrapper>
        <Breadcrumbs
          items={[
            {
              name:
                activeCarType == VehicleType.Demo
                  ? routes.demo.name
                  : routes.usedCars.name,
              path:
                activeCarType == VehicleType.Demo
                  ? routes.demo.path
                  : routes.usedCars.path,
            },
            {
              name: activeBrand.name,
              path:
                activeCarType == VehicleType.Demo
                  ? `${routes.brandsDemo.path}/${slugify(activeBrand.name)}`
                  : `${routes.brands.path}/${slugify(activeBrand.name)}`,
            },
            {
              name: activeModel ? activeModel.name : '',
              path:
                activeCarType == VehicleType.Demo
                  ? `${routes.brandsDemo.path}/${slugify(activeBrand.name)}/${activeModel ? slugify(activeModel.name) : ''}`
                  : `${routes.brands.path}/${slugify(activeBrand.name)}/${activeModel ? slugify(activeModel.name) : ''}`,
            },
          ]}
        />
      </BlockWrapper>

      <BlockWrapper>
        {openingParagraph && openingParagraph?.length !== 0 ? (
          <PageTitle>{openingParagraph[0]?.title}</PageTitle>
        ) : null}
        {openingParagraph && openingParagraph?.length !== 0 ? (
          <PageDescription>{openingParagraph[0]?.description}</PageDescription>
        ) : null}
        <LandingChildrenList
          activeParent={activeBrand}
          activeChild={activeModel as any}
          getChildLink={getModelsPagePath}
          title={
            <React.Fragment>
              <span>{activeBrand.name}</span> models
            </React.Fragment>
          }
          activeChildAs={
            !openingParagraph || !openingParagraph?.length ? 'h1' : undefined
          }
        />
        <MobileResults />
      </BlockWrapper>
      {newFilters ? (
        <NewSearchFilters />
      ) : (
        <SearchFilters
          pageType={activeCarType == VehicleType.Demo ? 'demo' : 'used'}
        />
      )}
      <SearchResults
        initialVehicles={vehicles}
        initialVehiclesMeta={vehiclesMeta}
        type={activeCarType}
        hideLocation={Boolean(kiaDealershipId)}
      />
    </React.Fragment>
  );
};

export default Model;
